import { useEffect, useState } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { useCreateCorrection } from "../../hooks/useBrandCorrections";
import type { BrandEditSourceType, TrustFeedbackTag } from "../../../../shared/types";
import { showToast } from "../../lib/toast";

interface CorrectionModalProps {
  open: boolean;
  onClose: () => void;
  brandId: string;
  sourceType: BrandEditSourceType;
  sourceId: string | null;
  contextHint?: string;
}

const TRUST_TAG_OPTIONS: Array<{ tag: TrustFeedbackTag; label: string }> = [
  { tag: "wrong_entity", label: "Wrong entity" },
  { tag: "stale", label: "Stale" },
  { tag: "off_topic", label: "Off topic" },
  { tag: "low_quality_source", label: "Low-quality source" },
  { tag: "useful", label: "Useful" },
  { tag: "acted_on", label: "Acted on" },
];

export function CorrectionModal({
  open,
  onClose,
  brandId,
  sourceType,
  sourceId,
  contextHint,
}: CorrectionModalProps) {
  const createCorrection = useCreateCorrection(brandId);
  const [text, setText] = useState("");
  const [tags, setTags] = useState<TrustFeedbackTag[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setText("");
      setTags([]);
      setSubmitError(null);
    }
  }, [open]);

  function toggleTag(tag: TrustFeedbackTag) {
    setTags((current) => current.includes(tag) ? current.filter((value) => value !== tag) : [...current, tag]);
  }

  async function handleSubmit() {
    setSubmitError(null);
    try {
      await createCorrection.mutateAsync({
        source_type: sourceType,
        source_id: sourceId,
        correction_text: text.trim(),
        trust_tags: tags,
      });
      // Verified path: assembleBrandContext() pulls getEditHistory(brandId, 10),
      // so the most recent 10 active corrections are available to future runs.
      showToast({
        title: "Correction saved",
        body: "The agent will include your most recent 10 active corrections in future runs.",
        action: {
          label: "View recent corrections",
          href: `/brand/${brandId}/corrections`,
        },
      });
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to save correction");
    }
  }

  const trimmedText = text.trim();
  const isDisabled = !sourceId || !trimmedText || tags.length === 0;
  let helperText: string | null = null;
  if (!sourceId) {
    helperText = "This item can't be corrected yet.";
  } else if (!trimmedText && tags.length === 0) {
    helperText = "Pick a tag and add a note for the agent to enable Save.";
  } else if (!trimmedText) {
    helperText = "Add a note for the agent.";
  } else if (tags.length === 0) {
    helperText = "Pick a tag for this correction.";
  }

  return (
    <Modal open={open} onClose={onClose} title={"What's wrong?"}>
      <div className="space-y-4">
        {contextHint && (
          <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">{contextHint}</p>
        )}
        <div>
          <p className="mb-2 text-sm font-medium text-slate-700">What kind of feedback is this?</p>
          <div className="flex flex-wrap gap-2">
            {TRUST_TAG_OPTIONS.map((option) => {
              const selected = tags.includes(option.tag);
              return (
                <button
                  key={option.tag}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleTag(option.tag)}
                  className={`rounded-full border px-3 py-1.5 text-sm ${selected ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-300 bg-white text-slate-700"}`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="correction-textarea">
            Tell the agent
          </label>
          <textarea
            id="correction-textarea"
            value={text}
            onChange={(e) => setText(e.currentTarget.value)}
            maxLength={500}
            rows={5}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            placeholder="Explain what is wrong and what the agent should do differently next time."
          />
          <p className="mt-1 text-xs text-slate-500">{text.length}/500</p>
        </div>
        {helperText && (
          <p id="correction-helper" className="text-sm text-slate-500">
            {helperText}
          </p>
        )}
        {submitError && (
          <p className="text-sm text-red-700" role="alert">
            {submitError}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            loading={createCorrection.isPending}
            disabled={isDisabled}
            aria-describedby={isDisabled && helperText ? "correction-helper" : undefined}
            onClick={() => void handleSubmit()}
          >
              Save correction
          </Button>
        </div>
      </div>
    </Modal>
  );
}
